import { useState } from 'react'
import SEO from '../components/SEO'
import ScrollReveal from '../components/ScrollReveal'
import './Careers.css'
import '../styles/shared-hero.css'

function Careers() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setStatus('submitting')

    const form = e.currentTarget
    const formData = new FormData(form)

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      })
      setStatus('success')
      form.reset()
      setTimeout(() => setStatus('idle'), 5000)
    } catch {
      setStatus('error')
      setTimeout(() => setStatus('idle'), 3000)
    }
  }

  return (
    <>
      <SEO 
        title="Careers - Join as a Fractional CTO" 
        description="Are you an experienced technology leader? Join our network of fractional CTOs and help growing companies succeed."
        path="/careers"
      />
      
      {/* Hero Section */}
      <section className="careers-hero page-hero">
        <div className="page-hero-content careers-hero-content">
          <ScrollReveal>
            <h1>Join our network of <em>fractional CTOs</em></h1>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <p className="careers-hero-description">
              Are you an experienced technology leader looking for flexible, high-impact work? 
              We're building a network of exceptional fractional CTOs to serve growing companies.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="why-join-section">
        <div className="section-grid">
          <div className="section-label">
            Why Join
            <span></span>
          </div>
          <div className="why-join-content">
            <ScrollReveal>
              <h2>Work that fits your life</h2>
            </ScrollReveal>
            <div className="benefits-list">
              <ScrollReveal delay={100}>
                <div className="benefit-item-careers">
                  <h3>Flexible Engagements</h3>
                  <p>Choose the projects and time commitments that work for you. Work with multiple companies or focus on one—it's your choice.</p>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <div className="benefit-item-careers">
                  <h3>Vetted Opportunities</h3>
                  <p>We evaluate every client to ensure they're ready for fractional leadership and can benefit from your expertise.</p>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={300}>
                <div className="benefit-item-careers">
                  <h3>Support Network</h3>
                  <p>Connect with other fractional CTOs, share challenges, and access resources to help you succeed in your engagements.</p>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={400}>
                <div className="benefit-item-careers">
                  <h3>Fair Compensation</h3>
                  <p>We believe in transparent, fair rates that reflect the value experienced technology leaders bring to growing companies.</p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section className="who-looking-section">
        <div className="section-grid">
          <div className="section-label">
            Who We Need
            <span></span>
          </div>
          <ScrollReveal className="who-looking-content">
            <h2>Who we're looking for</h2>
            <ul className="requirements-list">
              <li>
                <strong>15+ years</strong> of technology experience, with at least 5 years in leadership roles
              </li>
              <li>
                <strong>Track record</strong> of building and scaling engineering teams and systems
              </li>
              <li>
                <strong>Business acumen</strong> — you understand that technology serves business objectives
              </li>
              <li>
                <strong>Communication skills</strong> — you can translate technical concepts for non-technical stakeholders
              </li>
              <li>
                <strong>Pragmatism</strong> — you know when to push for perfection and when "good enough" is right
              </li>
              <li>
                <strong>UK or UAE based</strong> able to work comfortably with UK or UAE business culture
              </li>
            </ul>
          </ScrollReveal>
        </div>
      </section>

      {/* Application Form */}
      <section className="application-section">
        <div className="application-container">
          <ScrollReveal className="application-content">
            <h2>Apply to join</h2>
            <p>
              Tell us about your background and experience. We'll review your application 
              and reach out if there's a potential fit.
            </p>
          </ScrollReveal>

          <ScrollReveal className="application-form-wrapper" delay={200}>
            <form className="application-form" name="cto-application" onSubmit={handleSubmit} data-netlify="true" data-netlify-honeypot="bot-field">
              <input type="hidden" name="form-name" value="cto-application" />
              <p className="hidden-field">
                <label>Leave this field empty<input name="bot-field" tabIndex={-1} autoComplete="off" /></label>
              </p>

              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name"
                  name="name" 
                  placeholder="Your name" 
                  required 
                  disabled={status === 'submitting'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="your@email.com"
                  required
                  disabled={status === 'submitting'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="linkedin">LinkedIn Profile *</label>
                <input
                  type="url"
                  id="linkedin"
                  name="linkedin"
                  placeholder="https://linkedin.com/in/yourprofile"
                  required
                  disabled={status === 'submitting'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="experience">Years of Technology Leadership Experience *</label>
                <select id="experience" name="experience" required disabled={status === 'submitting'}>
                  <option value="">Select...</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10-15">10-15 years</option>
                  <option value="15+">15+ years</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="background">Tell us about your background and the types of companies you've helped *</label>
                <textarea
                  id="background"
                  name="background"
                  rows={5}
                  placeholder="Describe your experience, industries you've worked in, team sizes you've managed..."
                  required
                  disabled={status === 'submitting'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="availability">What type of engagements are you interested in? *</label>
                <select id="availability" name="availability" required disabled={status === 'submitting'}>
                  <option value="">Select...</option>
                  <option value="advisory">Advisory (1-2 days/week)</option>
                  <option value="project">Project-based</option>
                  <option value="interim">Interim/Full-time</option>
                  <option value="flexible">Flexible / All of the above</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="additional">Anything else you'd like us to know?</label>
                <textarea
                  id="additional"
                  name="additional"
                  rows={3}
                  placeholder="Specific expertise, location, availability constraints..."
                  disabled={status === 'submitting'}
                />
              </div>

              <button 
                type="submit" 
                className="cta-button"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' && 'Submitting...'}
                {status === 'success' && 'Application Submitted!'}
                {status === 'error' && 'Error - Try Again'}
                {status === 'idle' && 'Submit Application'}
              </button>
            </form>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}

export default Careers
